import React, { useEffect, useState } from 'react';
import { Storage, translate } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import { ValidatedField } from 'react-jhipster';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateEntity as updateBank } from 'app/entities/bank/bank.reducer';
import { AUTHORITIES } from 'app/config/constants';

const ALL_QUOTATION_STATUSES = ['Bought', 'Transferred', 'Sold'];

export function CancellationSetting() {
  const dispatch = useAppDispatch();
  const account = useAppSelector(state => state.authentication.account);
  const bankEntity = useAppSelector(state => state.bank.entity);

  const userRoleAccessList = useAppSelector(state => state.userManagement.userRoleAccessList);
  const isUserAuthorizedToEdit =
    userRoleAccessList.length === 0 || userRoleAccessList.some(role => role.bankId === bankEntity?.id);

  const [collapsed, setCollapsed] = useState(false);
  const [minDaysToCancel, setMinDaysToCancel] = useState('');
  const [maxDaysToCancel, setMaxDaysToCancel] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  const [errorMin, setErrorMin] = useState<string | null>(null);
  const [errorMax, setErrorMax] = useState<string | null>(null);
  const [errorRange, setErrorRange] = useState<string | null>(null);
  const [errorRequired, setErrorRequired] = useState<string | null>(null);

  // Any field filled means all must be filled
  const anyFilled = !!minDaysToCancel || !!maxDaysToCancel || selectedStatuses.length > 0;
  const allFilled = !!minDaysToCancel && !!maxDaysToCancel && selectedStatuses.length > 0;
  const isPartiallyFilled = anyFilled && !allFilled;

  const isRTL = Storage.session.get('locale') === 'ar-ly';

  const validateRange = (min: string, max: string) => {
    if (min && max && Number(min) > Number(max)) {
      setErrorRange(translate('comexApp.bank.details.cancellationSetting.errorMinGtMax'));
    } else {
      setErrorRange(null);
    }
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setMinDaysToCancel(val);
    setErrorMin(val === '0' ? translate('comexApp.bank.details.cancellationSetting.errorZero') : null);
    setErrorRequired(null);
    validateRange(val, maxDaysToCancel);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setMaxDaysToCancel(val);
    setErrorMax(val === '0' ? translate('comexApp.bank.details.cancellationSetting.errorZero') : null);
    setErrorRequired(null);
    validateRange(minDaysToCancel, val);
  };

  const handleStatusChange = (event: SelectChangeEvent<typeof selectedStatuses>) => {
    const { value } = event.target;
    setSelectedStatuses(typeof value === 'string' ? value.split(',') : [...value]);
    setErrorRequired(null);
  };

  const handleSubmit = () => {
    // All-or-nothing: if any field is filled, all must be filled
    if (isPartiallyFilled) {
      setErrorRequired(translate('comexApp.bank.details.cancellationSetting.errorAllRequired'));
      return;
    }
    setErrorRequired(null);
    const updatedBank = {
      ...bankEntity,
      minDaysToCancel: minDaysToCancel ? Number(minDaysToCancel) : null,
      maxDaysToCancel: maxDaysToCancel ? Number(maxDaysToCancel) : null,
      allowedStatusToCancel: selectedStatuses.length > 0 ? selectedStatuses.join(',') : null,
    };
    dispatch(updateBank(updatedBank));
  };

  useEffect(() => {
    if (bankEntity?.id !== undefined) {
      setMinDaysToCancel(bankEntity?.minDaysToCancel != null ? String(bankEntity.minDaysToCancel) : '');
      setMaxDaysToCancel(bankEntity?.maxDaysToCancel != null ? String(bankEntity.maxDaysToCancel) : '');
      if (bankEntity?.allowedStatusToCancel) {
        setSelectedStatuses(bankEntity.allowedStatusToCancel.split(',').map((s: string) => s.trim()));
      } else {
        setSelectedStatuses([]);
      }
    }
  }, [bankEntity]);

  useEffect(() => {
    if (errorMin) setErrorMin(translate('comexApp.bank.details.cancellationSetting.errorZero'));
    if (errorMax) setErrorMax(translate('comexApp.bank.details.cancellationSetting.errorZero'));
    if (errorRange) setErrorRange(translate('comexApp.bank.details.cancellationSetting.errorMinGtMax'));
    if (errorRequired) setErrorRequired(translate('comexApp.bank.details.cancellationSetting.errorAllRequired'));
  }, [Storage.session.get('locale')]);

  const isSaveDisabled =
    !isUserAuthorizedToEdit ||
    !!errorMin || !!errorMax || !!errorRange ||
    (account?.authorities?.includes(AUTHORITIES.ALSHMOKH_USER) &&
      !account?.authorities?.includes(AUTHORITIES.SUBADMIN_BANK));

  return (
    <div className="collapse-section">
      <Grid container className="align-items-center">
        <Grid item xs={10} sm={11} md={11.5}>
          <Typography variant="h5" component="div" data-cy="cancellationSettingHeading">
            {translate('comexApp.bank.details.cancellationSetting.title')}
          </Typography>
        </Grid>
        <Grid item xs={2} sm={1} md={0.5}>
          <IconButton aria-label="collapse" onClick={() => setCollapsed(prev => !prev)}>
            {collapsed ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </IconButton>
        </Grid>
      </Grid>

      <Collapse in={collapsed}>
        <br />

        {/* Min Days */}
        <ValidatedField
          label={translate('comexApp.bank.details.cancellationSetting.minDaysToCancel')}
          id="minDaysToCancel"
          name="minDaysToCancel"
          data-cy="minDaysToCancel"
          type="text"
          value={minDaysToCancel}
          onChange={handleMinChange}
          onInput={(e: React.FormEvent<HTMLInputElement>) => {
            (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/\D/g, '');
          }}
          pattern="[0-9]*"
          inputMode="numeric"
          min="1"
          disabled={!isUserAuthorizedToEdit}
        />
        {errorMin && <FormHelperText error sx={{ textAlign: isRTL ? 'right' : 'left', mb: 1 }}>{errorMin}</FormHelperText>}

        <br />

        {/* Max Days */}
        <ValidatedField
          label={translate('comexApp.bank.details.cancellationSetting.maxDaysToCancel')}
          id="maxDaysToCancel"
          name="maxDaysToCancel"
          data-cy="maxDaysToCancel"
          type="text"
          value={maxDaysToCancel}
          onChange={handleMaxChange}
          onInput={(e: React.FormEvent<HTMLInputElement>) => {
            (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/\D/g, '');
          }}
          pattern="[0-9]*"
          inputMode="numeric"
          min="1"
          disabled={!isUserAuthorizedToEdit}
        />
        {errorMax && <FormHelperText error sx={{ textAlign: isRTL ? 'right' : 'left', mb: 1 }}>{errorMax}</FormHelperText>}
        {errorRange && <FormHelperText error sx={{ textAlign: isRTL ? 'right' : 'left', mb: 1 }}>{errorRange}</FormHelperText>}

        <br />

        {/* Allowed Statuses — multi-select dropdown */}
        <Box>
          <label
            htmlFor="allowedStatusToCancel"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 400,
              fontSize: '1rem',
              color: 'inherit',
            }}
          >
            {translate('comexApp.bank.details.cancellationSetting.allowedStatusToCancel')}
          </label>
          <Select
            id="allowedStatusToCancel"
            multiple
            fullWidth
            displayEmpty
            value={selectedStatuses}
            onChange={handleStatusChange}
            disabled={!isUserAuthorizedToEdit}
            input={<OutlinedInput size="small" />}
            MenuProps={{ autoFocus: false }}
            sx={{ backgroundColor: '#fff', fontSize: '1rem' }}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <span style={{ color: '#aaa' }}>
                  {translate('comexApp.bank.details.cancellationSetting.statusPlaceholder')}
                </span>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(val => (
                    <Chip key={val} label={val} size="small" />
                  ))}
                </Box>
              )
            }
          >
            {ALL_QUOTATION_STATUSES.map(status => (
              <MenuItem key={status} value={status}>
                <input
                  type="checkbox"
                  readOnly
                  checked={selectedStatuses.includes(status)}
                  style={{ marginRight: 8 }}
                />
                {status}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <br />

        {errorRequired && (
          <FormHelperText error sx={{ textAlign: isRTL ? 'right' : 'left', mb: 1 }}>
            {errorRequired}
          </FormHelperText>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button color="primary" onClick={handleSubmit} disabled={isSaveDisabled}>
            <FontAwesomeIcon icon="plus" /> {translate('comexApp.bank.details.update')}
          </Button>
        </Box>
      </Collapse>
    </div>
  );
}
